import type { LensOperation } from '~/hooks/apis/reporting/useLensChange';

import type { CardLens, LensCard, LensDataset } from '~/types/leLens';

type Definition = Pick<CardLens, 'title' | 'description' | 'spec'>;
type Fields = Record<string, unknown>;

const SERVER_OWNED_CARD_FIELDS = ['contract', 'refusal'];
const AUTHORED_DATASET_FIELDS = new Set([
  'name',
  'description',
  'sql',
  'columns',
  'fields',
  'dimensions',
  'dimensionLabels',
  'dateFilter',
  'grain',
  'maxAgeSeconds',
]);
const same = (left: unknown, right: unknown) => JSON.stringify(left) === JSON.stringify(right);
const plainObject = (value: unknown): value is Fields =>
  Boolean(value) && typeof value === 'object' && !Array.isArray(value);

function patchTowards(before: unknown, after: unknown): unknown {
  if (after === undefined) return null;
  if (!plainObject(before) || !plainObject(after)) return after;
  const keys = [...new Set([...Object.keys(before), ...Object.keys(after)])];
  return Object.fromEntries(
    keys.filter((key) => !same(before[key], after[key])).map((key) => [key, patchTowards(before[key], after[key])]),
  );
}

function changedFields(before: Fields, after: Fields): Fields {
  return patchTowards(before, after) as Fields;
}

function cardOperation(before: LensCard | undefined, after: LensCard): LensOperation | null {
  const { id, ...fields } = after;
  if (!before)
    return { type: 'card.create', id, changes: { ...fields, ...(fields.layout ? { overrideArrangement: true } : {}) } };
  const changes = changedFields(before as unknown as Fields, after as unknown as Fields);
  if (SERVER_OWNED_CARD_FIELDS.some((field) => field in changes)) return null;
  if (!Object.keys(changes).length) return null;
  return {
    type: 'card.update',
    id,
    changes: { ...changes, ...(changes.layout ? { layout: after.layout, overrideArrangement: true } : {}) },
  };
}

function datasetOperations(before: Array<LensDataset>, after: Array<LensDataset>): Array<LensOperation> | null {
  const operations: Array<LensOperation> = [];
  const previous = new Map(before.map((dataset) => [dataset.id, dataset]));
  for (const dataset of after) {
    const old = previous.get(dataset.id);
    const { id, source, ...fields } = dataset;
    if (!old && (source?.kind ?? 'sql') !== 'sql') return null;
    const changes = old ? changedFields(old as unknown as Fields, dataset as unknown as Fields) : (fields as Fields);
    if (Object.keys(changes).some((key) => !AUTHORED_DATASET_FIELDS.has(key))) return null;
    if (Object.keys(changes).length) operations.push({ type: old ? 'dataset.update' : 'dataset.create', id, changes });
  }
  const kept = new Set(after.map((dataset) => dataset.id));
  for (const dataset of before) {
    if (!kept.has(dataset.id) && !dataset.deletedAt) operations.push({ type: 'dataset.remove', id: dataset.id });
  }
  return operations;
}

/**
 * The card, Lens, spec and dataset operations that turn `before` into `after`, or null when the
 * change edits server-owned card or dataset state and must be written whole.
 */
export function lensOperationsBetween(before: Definition, after: Definition): Array<LensOperation> | null {
  const { cards: beforeCards, datasets: beforeDatasets, ...beforeSpec } = before.spec;
  const { cards: afterCards, datasets: afterDatasets, ...afterSpec } = after.spec;
  const datasetChanges = datasetOperations(beforeDatasets ?? [], afterDatasets ?? []);
  if (!datasetChanges) return null;
  const operations = datasetChanges.filter((operation) => operation.type !== 'dataset.remove');
  const lensChanges = changedFields(
    { title: before.title, description: before.description ?? null },
    { title: after.title, description: after.description ?? null },
  );
  if (Object.keys(lensChanges).length) operations.push({ type: 'lens.update', changes: lensChanges });
  const specChanges = changedFields(beforeSpec as Fields, afterSpec as Fields);
  if (Object.keys(specChanges).length) operations.push({ type: 'spec.update', changes: specChanges });
  const previous = new Map(beforeCards.map((card) => [card.id, card]));
  for (const card of afterCards) {
    const before = previous.get(card.id);
    const operation = cardOperation(before, card);
    if (operation) operations.push(operation);
    else if (before && !same(before, card)) return null;
  }
  const kept = new Set(afterCards.map((card) => card.id));
  for (const card of beforeCards) if (!kept.has(card.id)) operations.push({ type: 'card.remove', id: card.id });
  const shared = (cards: Array<LensCard>, other: Set<string>) =>
    cards.map((card) => card.id).filter((id) => other.has(id));
  const beforeIds = new Set(beforeCards.map((card) => card.id));
  if (!same(shared(beforeCards, kept), shared(afterCards, beforeIds)))
    operations.push({ type: 'cards.reorder', changes: { ids: afterCards.map((card) => card.id) } });
  return [...operations, ...datasetChanges.filter((operation) => operation.type === 'dataset.remove')];
}
